import type { CSSProperties, ReactNode } from 'react';
import { widgetRegistry, type WidgetName } from '@rmint/widgets/registry';

export interface ReadOnlyWidgetProps {
  name: string;
  attrs: Record<string, string>;
}

// Phase 1 inline-styled chip. Phase 4 replaces this with a live mount;
// design-system tokens land then. For now, a clearly-bounded box so the
// editor surface visually distinguishes a widget from its surrounding
// prose without coupling to Tailwind or workspace stylesheets.
const CHIP: CSSProperties = {
  display: 'block',
  border: '1px solid #cdd5e0',
  borderLeft: '3px solid #4a6fa5',
  borderRadius: 4,
  padding: '8px 12px',
  margin: '12px 0',
  background: '#f6f8fb',
  fontFamily: 'system-ui, sans-serif',
  fontSize: 13,
  pointerEvents: 'none',
  userSelect: 'none',
};

const CHIP_LABEL: CSSProperties = {
  display: 'block',
  marginBottom: 6,
  color: '#4a6fa5',
  fontSize: 11,
  textTransform: 'uppercase',
  letterSpacing: '0.05em',
};

const CHIP_PRE: CSSProperties = {
  fontSize: 12,
  margin: 0,
  padding: 0,
  background: 'transparent',
  color: '#374151',
  whiteSpace: 'pre-wrap',
  wordBreak: 'break-word',
};

const ERROR_CHIP: CSSProperties = {
  display: 'block',
  border: '1px solid #fca5a5',
  borderLeft: '3px solid #dc2626',
  borderRadius: 4,
  padding: '8px 12px',
  margin: '12px 0',
  background: '#fef2f2',
  color: '#7f1d1d',
  fontFamily: 'system-ui, sans-serif',
  fontSize: 13,
};

export function ReadOnlyWidget({ name, attrs }: ReadOnlyWidgetProps): ReactNode {
  const meta = widgetRegistry[name as WidgetName];
  if (!meta) {
    return (
      <div data-testid="widget-unknown" className="rmint-widget-placeholder" style={ERROR_CHIP}>
        <strong>unknown widget:</strong> {name}
      </div>
    );
  }

  const parsed = meta.schema.safeParse(attrs);
  if (!parsed.success) {
    return (
      <div
        data-testid="widget-invalid-props"
        className="rmint-widget-placeholder"
        style={ERROR_CHIP}
      >
        <strong>{name}</strong> — invalid props:{' '}
        {parsed.error.issues.map((i) => i.path.join('.')).join(', ')}
      </div>
    );
  }

  return (
    <div
      data-testid={`widget-${name}`}
      data-widget-name={name}
      className="rmint-widget-readonly"
      style={CHIP}
    >
      <span style={CHIP_LABEL}>widget · {name}</span>
      <strong>{meta.description}</strong>
      <pre style={CHIP_PRE}>{JSON.stringify(parsed.data, null, 2)}</pre>
    </div>
  );
}
